import { useMemo } from "react";
import { usePermissions } from "@/contexts/PermissionContext";

interface Business {
  id: number;
  [key: string]: any;
}

interface Branch {
  id: number;
  brand_id?: number;
  [key: string]: any;
}

interface Driver {
  id: number;
  branch_id?: number;
  [key: string]: any;
}

interface Order {
  id: number;
  branch_id?: number;
  [key: string]: any;
}

/**
 * Hook for filtering data based on user permissions and resource access
 */
export function useDataFiltering() {
  const { resourceAccess, hasBrandAccess, hasBranchAccess } = usePermissions();

  /**
   * Filter businesses based on user access
   */
  const filterBrands = useMemo(() => {
    return <T extends Business>(businesses: T[]): T[] => {
      if (resourceAccess.canAccessAll) {
        return businesses;
      }

      return businesses.filter((business) => hasBrandAccess(business.id));
    };
  }, [resourceAccess.canAccessAll, hasBrandAccess]);

  /**
   * Filter branches based on user access
   */
  const filterBranches = useMemo(() => {
    return <T extends Branch>(branches: T[]): T[] => {
      if (resourceAccess.canAccessAll) {
        return branches;
      }

      return branches.filter((branch) => {
        // Check direct branch access
        if (hasBranchAccess(branch.id)) {
          return true;
        }

        // Check business access if branch has brand_id
        if (branch.brand_id && hasBrandAccess(branch.brand_id)) {
          return true;
        }

        return false;
      });
    };
  }, [resourceAccess.canAccessAll, hasBranchAccess, hasBrandAccess]);

  /**
   * Filter drivers based on branch access
   */
  const filterDrivers = useMemo(() => {
    return <T extends Driver>(drivers: T[]): T[] => {
      if (resourceAccess.canAccessAll) {
        return drivers;
      }

      return drivers.filter((driver) => {
        if (!driver.branch_id) {
          return false;
        }
        return hasBranchAccess(driver.branch_id);
      });
    };
  }, [resourceAccess.canAccessAll, hasBranchAccess]);

  /**
   * Filter orders based on branch access
   */
  const filterOrders = useMemo(() => {
    return <T extends Order>(orders: T[]): T[] => {
      if (resourceAccess.canAccessAll) {
        return orders;
      }

      return orders.filter((order) => {
        if (!order.branch_id) {
          return false;
        }
        return hasBranchAccess(order.branch_id);
      });
    };
  }, [resourceAccess.canAccessAll, hasBranchAccess]);

  /**
   * Get accessible business IDs for API filtering
   */
  const getBrandFilter = useMemo(() => {
    return (): number[] | null => {
      if (resourceAccess.canAccessAll) {
        return null; // No filter needed, return all
      }
      return resourceAccess.brandIds;
    };
  }, [resourceAccess]);

  /**
   * Get accessible branch IDs for API filtering
   */
  const getBranchFilter = useMemo(() => {
    return (): number[] | null => {
      if (resourceAccess.canAccessAll) {
        return null; // No filter needed, return all
      }
      return resourceAccess.branchIds;
    };
  }, [resourceAccess]);

  /**
   * Check if a specific business is accessible
   */
  const isBrandAccessible = (brandId: number): boolean => {
    return hasBrandAccess(brandId);
  };

  /**
   * Check if a specific branch is accessible
   */
  const isBranchAccessible = (branchId: number): boolean => {
    return hasBranchAccess(branchId);
  };

  /**
   * Filter any array based on a resource ID field
   */
  const filterByResource = <T extends Record<string, any>>(
    items: T[],
    resourceType: "business" | "branch",
    resourceIdField: string,
  ): T[] => {
    if (resourceAccess.canAccessAll) {
      return items;
    }

    return items.filter((item) => {
      const resourceId = item[resourceIdField];
      if (typeof resourceId !== "number") {
        return false;
      }

      if (resourceType === "business") {
        return hasBrandAccess(resourceId);
      } else {
        return hasBranchAccess(resourceId);
      }
    });
  };

  return {
    // Filter functions
    filterBrands,
    filterBranches,
    filterDrivers,
    filterOrders,
    filterByResource,

    // Filter getters for API calls
    getBrandFilter,
    getBranchFilter,

    // Access checkers
    isBrandAccessible,
    isBranchAccessible,

    // Direct access to resource access info
    resourceAccess,
  };
}
